import React from "react";
import Seo from "../components/common/Seo";
import LegalLayout, {
  LegalSection,
  LegalSubheading,
  LegalText,
  LegalList,
  LegalCallout,
  LegalContact,
} from "../components/common/LegalLayout";

const EMAIL = "f_uo@infiniteloop-tech.com";

const TermsAndConditions: React.FC = () => {
  return (
    <>
      <Seo
        title="Terms &amp; conditions | Fork"
        description="The terms governing your use of Fork — accounts, user content, the medical disclaimer, liability and dispute resolution."
        path="/terms"
      />
      <LegalLayout
        title="Terms & conditions"
        lastUpdated="Last updated: December 13, 2025"
        otherHref="/privacy"
        otherLabel="Privacy policy"
        lede={
          <>
            Welcome to Fork AI, a service provided by Infinite Loop Tech, Inc.
            ("Company", "we", "us", or "our"). These Terms and Conditions
            ("Terms") govern your access to and use of our website, mobile
            application, and services (collectively, the "Service").
          </>
        }
        intro={
          <>
            By accessing or using the Service, you agree to be bound by these
            Terms. If you disagree with any part of the Terms, you may not
            access the Service.
          </>
        }
      >
        <LegalSection label="1." title="Acceptance of terms">
          <LegalText>
            By creating an account or using Fork AI, you acknowledge that you
            have read, understood, and agree to be bound by these Terms and our
            Privacy Policy. These Terms apply to all users of the Service,
            including without limitation users who are browsers, vendors,
            customers, merchants, and/or contributors of content.
          </LegalText>
          <LegalText last>
            You must be at least 13 years old to use this Service. By using the
            Service, you represent and warrant that you are at least 13 years of
            age.
          </LegalText>
        </LegalSection>

        <LegalSection label="2." title="Description of Service">
          <LegalText>
            Fork AI is a nutrition tracking application that uses artificial
            intelligence to analyze photos and voice recordings of meals to
            provide nutrition insights, calorie estimates, and dietary
            information. The Service includes:
          </LegalText>
          <LegalList
            items={[
              "Photo-based meal analysis using AI technology",
              "Voice-to-text meal logging",
              "Nutrition tracking and insights",
              "Personalized dietary recommendations",
              "Historical meal and nutrition data",
            ]}
          />
          <LegalText last>
            We reserve the right to modify, suspend, or discontinue the Service
            (or any part thereof) at any time with or without notice. We will
            not be liable to you or any third party for any modification,
            suspension, or discontinuance of the Service.
          </LegalText>
        </LegalSection>

        <LegalSection label="3." title="User accounts and registration">
          <LegalText>
            To access certain features of the Service, you must register for an
            account. When you register, you agree to:
          </LegalText>
          <LegalList
            items={[
              "Provide accurate, current, and complete information",
              "Maintain and promptly update your account information",
              "Maintain the security of your password and account",
              "Accept all risks of unauthorized access to your account",
              "Notify us immediately of any unauthorized use of your account",
            ]}
          />
          <LegalText last>
            You are responsible for all activities that occur under your
            account. We reserve the right to refuse service, terminate accounts,
            or remove or edit content at our sole discretion.
          </LegalText>
        </LegalSection>

        <LegalSection label="4." title="User content and conduct">
          <LegalText>
            You retain ownership of any content you submit to the Service,
            including photos, voice recordings, and text ("User Content"). By
            submitting User Content, you grant us a worldwide, non-exclusive,
            royalty-free license to use, reproduce, modify, and process your
            User Content solely for the purpose of providing and improving the
            Service.
          </LegalText>
          <LegalSubheading>You agree not to:</LegalSubheading>
          <LegalList
            items={[
              "Use the Service for any illegal purpose or in violation of any laws",
              "Upload content that is offensive, harmful, or violates others' rights",
              "Attempt to gain unauthorized access to the Service or related systems",
              "Interfere with or disrupt the Service or servers",
              "Use automated systems to access the Service without permission",
              "Reverse engineer or attempt to extract source code from the Service",
              "Impersonate any person or entity or misrepresent your affiliation",
            ]}
          />
        </LegalSection>

        <LegalSection label="5." title="Medical disclaimer">
          <LegalCallout title="Important notice">
            Fork AI is not a medical device and is not intended to diagnose,
            treat, cure, or prevent any disease. The nutrition information and
            insights provided by the Service are estimates based on AI analysis
            and should not be considered medical advice.
          </LegalCallout>
          <LegalText>
            You should always consult with a qualified healthcare provider
            before making any changes to your diet or nutrition plan. The
            Service is intended for informational and educational purposes only.
            We do not guarantee the accuracy, completeness, or reliability of
            any nutrition information provided.
          </LegalText>
          <LegalText last>
            If you have any medical conditions, allergies, or dietary
            restrictions, please consult with your healthcare provider before
            using the Service.
          </LegalText>
        </LegalSection>

        <LegalSection label="6." title="Intellectual property rights">
          <LegalText>
            The Service and its original content (excluding User Content),
            features, and functionality are and will remain the exclusive
            property of Infinite Loop Tech, Inc. and its licensors. The Service
            is protected by copyright, trademark, and other laws of both the
            United States and foreign countries.
          </LegalText>
          <LegalText last>
            Our trademarks, including "Fork AI" and associated logos, may not be
            used without our prior written permission. All other trademarks,
            service marks, and logos used on the Service are the property of
            their respective owners.
          </LegalText>
        </LegalSection>

        <LegalSection label="7." title="Third-party services">
          <LegalText>
            The Service may use third-party AI providers and services to analyze
            your photos and voice recordings. By using the Service, you
            acknowledge and agree that your content may be processed by these
            third-party providers in accordance with our Privacy Policy.
          </LegalText>
          <LegalText last>
            We are not responsible for the content, privacy policies, or
            practices of any third-party services. We encourage you to review
            the terms and privacy policies of any third-party services you
            access through our Service.
          </LegalText>
        </LegalSection>

        <LegalSection label="8." title="Limitation of liability">
          <LegalText>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL INFINITE
            LOOP TECH, INC., ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS,
            SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT
            LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER
            INTANGIBLE LOSSES, RESULTING FROM:
          </LegalText>
          <LegalList
            items={[
              "Your access to or use of or inability to access or use the Service",
              "Any conduct or content of any third party on the Service",
              "Any content obtained from the Service",
              "Unauthorized access, use, or alteration of your transmissions or content",
            ]}
          />
          <LegalText last>
            THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
            WITHOUT ANY WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED.
          </LegalText>
        </LegalSection>

        <LegalSection label="9." title="Indemnification">
          <LegalText last>
            You agree to defend, indemnify, and hold harmless Infinite Loop
            Tech, Inc. and its licensors, employees, contractors, agents,
            officers, and directors from and against any and all claims,
            damages, obligations, losses, liabilities, costs, or debt, and
            expenses arising from: (a) your use of and access to the Service;
            (b) your violation of any term of these Terms; (c) your violation of
            any third-party right, including without limitation any copyright,
            property, or privacy right; or (d) any claim that your User Content
            caused damage to a third party.
          </LegalText>
        </LegalSection>

        <LegalSection label="10." title="Termination">
          <LegalText>
            We may terminate or suspend your account and access to the Service
            immediately, without prior notice or liability, for any reason,
            including without limitation if you breach these Terms.
          </LegalText>
          <LegalText>
            Upon termination, your right to use the Service will immediately
            cease. If you wish to terminate your account, you may contact us to
            request account deletion.
          </LegalText>
          <LegalText last>
            All provisions of these Terms which by their nature should survive
            termination shall survive termination, including, without
            limitation, ownership provisions, warranty disclaimers, indemnity,
            and limitations of liability.
          </LegalText>
        </LegalSection>

        <LegalSection label="11." title="Governing law and dispute resolution">
          <LegalText>
            These Terms shall be governed and construed in accordance with the
            laws of the United States, without regard to its conflict of law
            provisions.
          </LegalText>
          <LegalText last>
            Any disputes arising out of or relating to these Terms or the
            Service shall be resolved through binding arbitration in accordance
            with the rules of the American Arbitration Association, except that
            either party may seek injunctive or other equitable relief in any
            court of competent jurisdiction.
          </LegalText>
        </LegalSection>

        <LegalSection label="12." title="Changes to terms">
          <LegalText>
            We reserve the right to modify or replace these Terms at any time at
            our sole discretion. If a revision is material, we will provide at
            least 30 days' notice prior to any new terms taking effect through
            email notification or a prominent notice on our Service.
          </LegalText>
          <LegalText last>
            By continuing to access or use our Service after those revisions
            become effective, you agree to be bound by the revised terms. If you
            do not agree to the new terms, you must stop using the Service.
          </LegalText>
        </LegalSection>

        <LegalSection label="13." title="General provisions">
          <LegalSubheading flush>Entire agreement</LegalSubheading>
          <LegalText last>
            These Terms, together with our Privacy Policy, constitute the entire
            agreement between you and Infinite Loop Tech, Inc. regarding the use
            of the Service.
          </LegalText>
          <LegalSubheading>Severability</LegalSubheading>
          <LegalText last>
            If any provision of these Terms is held to be invalid or
            unenforceable, such provision shall be struck and the remaining
            provisions shall be enforced.
          </LegalText>
          <LegalSubheading>Waiver</LegalSubheading>
          <LegalText last>
            Our failure to enforce any right or provision of these Terms will
            not be considered a waiver of those rights. The waiver of any such
            right or provision will be effective only if in writing and signed
            by a duly authorized representative of Infinite Loop Tech, Inc.
          </LegalText>
        </LegalSection>

        <LegalContact label="14." title="Contact information" email={EMAIL}>
          If you have any questions about these Terms, please contact us:
        </LegalContact>

        <p className="mt-4 text-[16px] leading-[1.65] text-dark-textSecondary">
          <strong className="text-white">Infinite Loop Tech, Inc.</strong>
          <br />
          Email: {EMAIL}
        </p>
      </LegalLayout>
    </>
  );
};

export default TermsAndConditions;
